import initAccordion from './accordion'

/** Start fetching this far before the sentinel scrolls into view, so the rows are usually there. */
const LOOKAHEAD = '800px'

/**
 * Loads the rest of the home incident list as the reader scrolls (`[ck-incidents-more]`).
 *
 * The worker ships the first 200 rows and ends the list with a sentinel whose attribute holds
 * the address of the next page (`/incidents/rows?offset=200`). When the sentinel comes near the
 * viewport we fetch that fragment, swap it in place of the sentinel, and initialise the new rows'
 * accordions. The fragment ends with the next sentinel, if any, so this repeats until the list
 * is complete. If a fetch fails the sentinel is left in place and tried again on the next scroll.
 */
export default function IncidentsMore() {
  if (!('IntersectionObserver' in window)) return

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue
        observer.unobserve(entry.target)
        load(entry.target as HTMLElement)
      }
    },
    { rootMargin: LOOKAHEAD },
  )

  const watch = (root: ParentNode) => {
    root.querySelectorAll<HTMLElement>('[ck-incidents-more]').forEach((el) => observer.observe(el))
  }

  const load = async (sentinel: HTMLElement) => {
    const url = sentinel.getAttribute('ck-incidents-more')
    if (!url || sentinel.hasAttribute('data-ck-loading')) return
    sentinel.setAttribute('data-ck-loading', '')
    try {
      const response = await fetch(url, { headers: { accept: 'text/html' } })
      if (!response.ok) throw new Error(`${response.status} for ${url}`)
      const fragment = document.createRange().createContextualFragment(await response.text())
      const rows = [...fragment.children] as HTMLElement[]
      sentinel.replaceWith(fragment)
      rows.forEach((row) => initAccordion(row))
      const next = rows.find((row) => row.matches('[ck-incidents-more]'))
      if (next) observer.observe(next)
    } catch (error) {
      console.error('Could not load more incidents', error)
      sentinel.removeAttribute('data-ck-loading')
      observer.observe(sentinel)
    }
  }

  watch(document)
}
